// src/pages/AppAdmin.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, onSnapshot, updateDoc, Timestamp, writeBatch, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/authContext';
import { updateDatabaseSchema } from '../utils/databaseUpdate';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { runVerseEnrichment, cancelEnrichment } from '../utils/databaseEnrich';
import { EnrichmentModal } from '../components/EnrichmentModal';
import { migrateReminderSettings } from '../utils/migrateReminderSettings';
import { ReminderQueueTester } from '../utils/reminderQueueTester';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRightIcon, 
  ChartBarIcon, 
  Cog6ToothIcon, 
  ClipboardDocumentListIcon,
  PlusIcon,
  BellIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import PendingOrganizations from '../components/admin/PendingOrganizations';
import { toast } from 'react-hot-toast';
// FCMTester removed - web app uses email-only notifications
import Layout from './Layout';
import Papa from 'papaparse';

const AppAdmin = () => {
  const { currentUser, isSuperAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('organizations');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [isEnrichmentModalOpen, setIsEnrichmentModalOpen] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState('');
  const [isMigratingNotificationDate, setIsMigratingNotificationDate] = useState(false);
  const [migrationNotificationDateStatus, setMigrationNotificationDateStatus] = useState('');
  const [selectedDisciple, setSelectedDisciple] = useState('');
  const [minutesFromNow, setMinutesFromNow] = useState(1);
  const [disciples, setDisciples] = useState([]);
  const [currentQueue, setCurrentQueue] = useState([]);
  const [orgStats, setOrgStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    subscribed: 0,
    withActiveSubscription: 0
  });
  const navigate = useNavigate();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // FCMTester removed - web app uses email-only notifications
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedStatusMessage, setSeedStatusMessage] = useState('');
  const [normalizeModalOpen, setNormalizeModalOpen] = useState(false);
  const [normalizeResult, setNormalizeResult] = useState({ updated: 0, checked: 0 });
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);
  const [cleanupFile, setCleanupFile] = useState(null);
  const [cleanupPreview, setCleanupPreview] = useState([]);
  const [cleanupParseError, setCleanupParseError] = useState('');
  const [cleanupScanResults, setCleanupScanResults] = useState([]);
  const [cleanupScanLoading, setCleanupScanLoading] = useState(false);
  const [cleanupScanError, setCleanupScanError] = useState('');
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);
  const [cleanupSummary, setCleanupSummary] = useState(null);
  const [cleanupProcessing, setCleanupProcessing] = useState(false);
  const [bibleUploadModalOpen, setBibleUploadModalOpen] = useState(false);
  const [bibleUploadFile, setBibleUploadFile] = useState(null);
  const [bibleUploadLang, setBibleUploadLang] = useState('en');
  const [bibleUploadName, setBibleUploadName] = useState('World English Bible');
  const [bibleUploadAbbr, setBibleUploadAbbr] = useState('WEB');
  const [bibleUploadId, setBibleUploadId] = useState('web');
  const [bibleUploadLoading, setBibleUploadLoading] = useState(false);
  const [bibleUploadSummary, setBibleUploadSummary] = useState(null);
  const [bibleUploadError, setBibleUploadError] = useState('');
  const [webReplaceModalOpen, setWebReplaceModalOpen] = useState(false);
  const [webReplaceLoading, setWebReplaceLoading] = useState(false);
  const [webReplacePreview, setWebReplacePreview] = useState(null);
  const [webReplaceResult, setWebReplaceResult] = useState(null);
  const [webReplaceError, setWebReplaceError] = useState('');
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [correctionReport, setCorrectionReport] = useState('');
  const [videoCompressionModalOpen, setVideoCompressionModalOpen] = useState(false);
  const [videoCompressionSettings, setVideoCompressionSettings] = useState({
    maxHeight: 1280,
    targetBitrate: '1200k',
    codec: 'libx264',
    preset: 'veryfast',
    pixelFormat: 'yuv420p'
  });
  const [videoCompressionLoading, setVideoCompressionLoading] = useState(false);

  // Global search function for SuperAdmins
  const handleSearch = async () => {
    if (!searchTerm || !isSuperAdmin) return;
    
    setIsLoading(true);
    try {
      const collectionRef = collection(db, searchType);
      const q = query(
        collectionRef,
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff')
      );
      
      const querySnapshot = await getDocs(q);
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerseUpdate = async () => {
    if (!window.confirm('Are you sure you want to update all verses? This process cannot be interrupted.')) {
      return;
    }

    setIsUpdating(true);
    setUpdateStatus('Starting verse database update...');

    try {
      const updatedCount = await updateDatabaseSchema();
      setUpdateStatus(`Successfully updated ${updatedCount} verses!`);
    } catch (error) {
      console.error('Error updating verses:', error);
      setUpdateStatus(`Error: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleVerseEnrichment = async (selectedFields) => {
    setIsEnriching(true);
    setUpdateStatus('Starting verse analysis...');

    try {
      await runVerseEnrichment(
        (status) => setUpdateStatus(status),
        selectedFields
      );
    } catch (error) {
      console.error('Error enriching verses:', error);
      setUpdateStatus(`Error: ${error.message}`);
    } finally {
      setIsEnriching(false);
      setIsUpdating(false);
    }
  };

  const handleCancelEnrichment = async () => {
    if (window.confirm('Are you sure you want to cancel the enrichment process?')) {
      try {
        await cancelEnrichment();
        setUpdateStatus('Enrichment cancelled.');
      } catch (error) {
        console.error('Error cancelling enrichment:', error);
        setUpdateStatus(`Error cancelling: ${error.message}`);
      }
    }
  };

  // Add migration handler
  const handleMigration = async () => {
    if (!window.confirm('Are you sure you want to migrate all reminder settings? This will update all disciples\' reminder configurations.')) {
      return;
    }

    setIsMigrating(true);
    setMigrationStatus('Starting reminder settings migration...');

    try {
      const result = await migrateReminderSettings(currentUser);
      if (result.success) {
        setMigrationStatus(`Migration completed successfully! Updated ${result.migratedCount} disciples. Errors: ${result.errorCount}`);
      } else {
        setMigrationStatus(`Migration failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Migration error:', error);
      setMigrationStatus(`Migration failed: ${error.message}`);
    } finally {
      setIsMigrating(false);
    }
  };

  // Migration handler for nextNotificationDateAndTime
  const handleMigrateNextNotificationDateAndTime = async () => {
    const instructions = `Run this migration locally using the Node.js script:

1. Open your terminal
2. Navigate to the mobile app directory:
   cd /Users/pezbook/Documents/Code/TogetherOnMission

3. Set your service account key:
   export GOOGLE_APPLICATION_CREDENTIALS="/Users/pezbook/Documents/Code/TogetherOnMission/scripts/together-on-mission-dev-firebase-adminsdk-fbsvc-a30ff9a369.json"

4. Run the script:
   node scripts/migrate-next-notification-date.js

The script will:
- Show progress for each disciple
- Display: Disciple name, found date, added date
- Show a summary at the end

See scripts/README_MIGRATE_NOTIFICATION_DATE.md for full instructions.`;

    // Copy instructions to clipboard
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(instructions);
      toast.success('Instructions copied to clipboard!');
    }

    setMigrationNotificationDateStatus(instructions);
  };

  // NEW: Seeder function for Missional Scale Levels
  const seedMissionalScaleLevels = async () => {
    console.log('[Seeder] Starting seedMissionalScaleLevels function...');
    if (!window.confirm('Are you sure you want to seed/overwrite the Missional Scale Levels collection? This will update 20 documents with phrase, description, apologeticsTopics, and lifeApplication fields.')) {
      console.log('[Seeder] Seeding cancelled by user.');
      return;
    }

    setIsSeeding(true);
    setSeedStatusMessage('Starting Missional Scale seeding...');

    // Expanded data including new fields
    const scaleData = {
       // Levels -10 to 0: Focus on Apologetics & Foundational Understanding
      '-10': {
        phrase: "No awareness of spiritual reality",
        description: "No belief in any spiritual reality. They live according to a secular worldview, relying on science and reason.",
        apologeticsTopics: ["Arguments for God's Existence (Cosmological, Teleological)", "Is there Objective Morality?", "Limitations of Materialism/Naturalism", "The Problem of Evil (Introduction)"],
        lifeApplication: "Consider the possibility that there might be more to reality than the purely material world. Reflect on where meaning, purpose, and morality ultimately come from. Be open to exploring different perspectives on life's big questions, even if they seem counterintuitive to a purely secular worldview."
      },
      '-9': {
        phrase: "There may be something",
        description: "Agnostic. Skeptical of any religious or spiritual claims, but they are open to the idea of a God.",
        apologeticsTopics: ["Can We Know Truth?", "Evidence for God's Existence (Fine-tuning Argument)", "Pascal's Wager (as thought experiment)", "Agnosticism vs. Atheism", "Reliability of Historical Inquiry"],
        lifeApplication: "Actively explore the fundamental questions: Where did we come from? Why are we here? What is right and wrong? Where are we going? Examine the evidence presented by various worldviews, including theism. Consider the potential implications if a higher power or spiritual reality does exist."
      },
       '-8': {
        phrase: "Some Awareness of God",
        description: "They believe in a higher spiritual force or multiple deities, practice meditation, rituals, or spiritual philosophies.",
        apologeticsTopics: ["Arguments for Monotheism", "Critique of Pantheism/Polytheism", "Concept of Divine Revelation", "Is God Personal or Impersonal?", "Comparing World Religions (Introduction)"],
        lifeApplication: "Reflect on the nature of the spiritual force or deity you perceive. Is it a personal being or an impersonal force? Does it communicate or interact with humanity? Compare your understanding with the concept of the personal, creator God presented in Judeo-Christian thought."
      },
      '-7': {
        phrase: "God is personal, but don't know what His Name is",
        description: "They hold monotheistic beliefs, practice religious traditions, and seek to live morally but do not accept Jesus as divine.",
        apologeticsTopics: ["Historical Evidence for Jesus of Nazareth", "Uniqueness of Jesus Among Religious Figures", "Introduction to the Bible (Origin, Reliability)", "Did Jesus Claim to Be God?", "Prophecy as Evidence (Introduction)"],
        lifeApplication: "Investigate the specific claims made by different monotheistic faiths about God's identity and relationship with humanity, paying particular attention to the claims of Christianity regarding Jesus Christ. Examine the historical evidence for Jesus and consider his unique statements about himself."
      },
       '-6': {
        phrase: "Some Interest in Jesus Christ",
        description: "They recognize Jesus as historically significant but are unsure if He is truly the only way to God.",
        apologeticsTopics: ["Evidence for the Resurrection of Jesus", "The Trilemma: Lord, Liar, or Lunatic?", "Messianic Prophecies Fulfilled in Jesus", "Why is Jesus the *Only* Way? (Addressing Pluralism)", "Reliability of the Gospel Accounts"],
        lifeApplication: "Focus specifically on the claims Jesus made about his own identity and purpose. Evaluate the historical evidence for his resurrection, as this is central to the Christian faith. Read the Gospel accounts (e.g., Mark or Luke) to understand his life and teachings directly."
      },
       '-5': {
        phrase: "Decided to investigate Jesus",
        description: "They believe in a monotheistic God and are drawn to Christianity but have not developed a personal relationship with Jesus.",
        apologeticsTopics: ["What is the Gospel Message?", "Understanding Sin (Personal Relevance)", "The Concept of Atonement and Substitution", "Jesus' Solution to Sin and Death", "Grace vs. Earning Salvation"],
        lifeApplication: "Read one of the Gospels (perhaps John) with an open mind, seeking to understand who Jesus is and what he offers. Reflect honestly on the concept of sin and whether you see it in your own life. Consider the Christian claim that Jesus offers forgiveness and a restored relationship with God."
      },
      '-4': {
        phrase: "Grasp the truth about Jesus",
        description: "They understand the basic Christian message but remain undecided about following Jesus. Understanding fundamentals of the Gospel",
        apologeticsTopics: ["What Does it Mean to Believe/Have Faith?", "Understanding Repentance", "The Cost of Discipleship", "Evidence for the Bible's Inspiration/Authority", "Common Objections to Christianity (Addressing Doubts)"],
        lifeApplication: "Identify any remaining intellectual or emotional barriers preventing a decision to follow Christ. Count the potential cost of becoming a disciple. Discuss your questions and doubts with a trusted Christian who can offer biblical perspectives. Compare the claims of the Gospel with your own life experiences and understanding."
      },
       '-3': {
        phrase: "Thinks Jesus is real. Don't know Him yet",
        description: "They are open to Christianity and see its value but have not yet made a personal commitment.",
        apologeticsTopics: ["The Call to Personal Faith and Repentance", "Assurance of Salvation (Introduction)", "The Role of the Holy Spirit (Introduction)", "Making a Personal Commitment to Christ"],
        lifeApplication: "Consider moving beyond intellectual agreement to personal trust and commitment. What prevents you from surrendering your life to Christ? Pray, asking God to reveal Himself to you personally and to help you overcome any obstacles to faith. Talk through the decision with a mentor."
      },
      '-2': {
        phrase: "Understands they have a sin problem",
        description: "Recognition of personal sin problem in relation to God. They have started acknowledging that their actions fall short of God's standard but have not yet accepted Jesus.",
        apologeticsTopics: ["The Meaning of Repentance (Turning From Sin)", "Faith as Active Trust and Reliance", "Importance of Christian Community/Church (Introduction)", "Water Baptism (Meaning and Significance)"],
        lifeApplication: "Move from acknowledging sin generally to specific repentance – turning away from known wrongdoings and attitudes. Express your faith in Jesus through prayer, asking for forgiveness and leadership in your life. Seek out a community of believers for support and encouragement."
      },
      '-1': {
        phrase: "Acceptance of the Truth",
        description: "Repentance - Decision to surrender to Christ. They have realized the need to turn away from their past mistakes and are making efforts to change.",
        apologeticsTopics: ["Lordship of Christ (What it Means)", "The Fruit of the Spirit (Introduction - Galatians 5)", "Introduction to Spiritual Disciplines (Prayer, Bible Reading)"],
        lifeApplication: "Begin incorporating basic spiritual disciplines into your daily life, such as prayer and reading the Bible (start with the Gospels or Acts). Seek baptism as an outward expression of your inward decision. Share your commitment with another believer or mentor for accountability and guidance."
      },
       '0': {
        phrase: "Brand New Believer",
        description: "They recently accepted Jesus and are beginning their journey in the Christian faith. Conversion: New Creation in Christ because received Him as Savior",
        apologeticsTopics: [], // Less focus on apologetics, more on foundations
        lifeApplication: "Focus on gaining assurance of your salvation through God's promises in scripture (e.g., John 10:28-29, 1 John 5:11-13). Connect meaningfully with a local church and a small group for fellowship and learning. Start developing habits of regular prayer and Bible reading. Share your story (testimony) of coming to faith with someone."
      },
      
      // Levels 1-9: Focus on Growth, Discipleship & Application
       '1': {
        phrase: "Basic Faith in place",
        description: "Assurance of salvation. They have confidence in their relationship with Jesus and no longer doubt their salvation.",
        apologeticsTopics: [],
        lifeApplication: "Develop consistency in basic spiritual disciplines: set aside regular time for prayer and Bible study. Build meaningful relationships within your church community. Begin learning about spiritual gifts and how God might want to use you. Understand the connection between faith and obedience to God's Word."
      },
       '2': {
        phrase: "Understands their identity in Christ",
        description: "They have begun identifying as a Christian and are integrating faith into their daily routines and decisions. Adoption of Christian identity",
        apologeticsTopics: [],
        lifeApplication: "Study and meditate on scriptures related to your identity in Christ (e.g., child of God, forgiven, righteous, new creation - see Romans 8, 2 Cor 5:17, Eph 1-2). Consciously make daily choices that align with this new identity. Learn to recognize and resist temptation by standing on the truth of who you are in Christ. Memorize key identity verses."
      },
       '3': {
        phrase: "Experiencing Change in their life",
        description: "They are developing habits that align with Christian teachings, working on personal integrity and spiritual discipline.",
        apologeticsTopics: [],
        lifeApplication: "Actively identify specific areas where old habits or thought patterns conflict with biblical teaching (Colossians 3:5-17). Practice putting off the old self and putting on the new self through the power of the Holy Spirit. Work on developing the Fruit of the Spirit (Galatians 5:22-23). Seek accountability from mature believers for areas of struggle."
      },
       '4': {
        phrase: "Developing stronger faith",
        description: "This person regularly reads and studies the Bible, using devotionals or study groups to deepen their understanding.",
        apologeticsTopics: [],
        lifeApplication: "Move beyond surface-level Bible reading to deeper study methods (e.g., inductive study, verse mapping). Develop a more intentional and varied prayer life (adoration, confession, thanksgiving, supplication - ACTS model). Learn to actively trust God during trials and uncertainty, recalling His faithfulness. Step out in faith in practical ways as God leads."
      },
      '5': {
        phrase: "Learning Christian Disciplines",
        description: "They study theological concepts and apply them to their daily lives, ensuring that their faith is based on sound doctrine.",
        apologeticsTopics: [],
        lifeApplication: "Intentionally practice a wider range of spiritual disciplines, such as fasting, solitude, service, stewardship, journaling, etc. Study foundational Christian doctrines (e.g., nature of God, person and work of Christ, Holy Spirit, Scripture, Church) to build a robust theological framework. Learn how to articulate your beliefs clearly and biblically."
      },
      '6': {
        phrase: "Assimilated into the Church",
        description: "This person actively seeks to understand and use their God-given talents to serve others in the church.",
        apologeticsTopics: [],
        lifeApplication: "Identify your spiritual gifts and find a regular place to serve within the local church body that aligns with those gifts. Actively participate in the life of the community through small groups, fellowship, and mutual encouragement. Practice biblical hospitality. Develop habits of generous giving (time, talent, treasure) as an act of worship."
      },
       '7': {
        phrase: "Sharing their faith with others",
        description: "They regularly share their faith with non-believers, organize outreach events, and disciple new Christians.",
        apologeticsTopics: [],
        lifeApplication: "Learn simple and effective ways to share the Gospel message and your personal testimony. Intentionally build relationships with non-believers, praying for opportunities to share Christ's love and truth. Overcome fear by relying on the Holy Spirit and practicing sharing in safe environments. Invite people to church, outreach events, or studies."
      },
       '8': {
        phrase: "Growing in Leadership",
        description: "They are deeply involved in church leadership, teaching Bible studies, and helping others grow in their faith.",
        apologeticsTopics: [],
        lifeApplication: "Begin intentionally mentoring or discipling one or two younger believers, helping them grow in their faith journey. Take initiative in ministry areas, identifying needs and developing solutions. Develop key leadership qualities like effective communication, delegation, conflict resolution, and encouragement. Study biblical models of leadership."
      },
       '9': {
        phrase: "Living into their purpose in Christ",
        description: "This person actively mentors others in their faith, seeks leadership roles in ministry, and is planning ways to serve God long-term.",
        apologeticsTopics: [],
        lifeApplication: "Focus on spiritual multiplication – discipling others who will, in turn, disciple others. Seek God's specific guidance for your unique long-term calling and ministry contribution. Steward your influence, resources, and opportunities for maximum kingdom impact. Lead with humility, integrity, and a heart focused on serving God and others."
      }
    };

    try {
      console.log('[Seeder] Creating write batch...');
      const batch = writeBatch(db);
      
      for (let i = -10; i <= 9; i++) {
        const levelStr = i.toString();
        const docRef = doc(db, 'missionalScaleLevels', levelStr);
        const currentLevelData = scaleData[levelStr];
        
        // Ensure data exists for the level before proceeding
        if (!currentLevelData) {
            console.warn(`[Seeder] No data found for level ${levelStr}, skipping.`);
            continue; 
        }

        const data = {
          level: i,
          phrase: currentLevelData.phrase || `Phrase for level ${i}`,
          description: currentLevelData.description || `Description for level ${i}`,
          apologeticsTopics: currentLevelData.apologeticsTopics || [], // Add new field with default
          lifeApplication: currentLevelData.lifeApplication || '' // Add new field with default
        };
        console.log(`[Seeder] Adding level ${levelStr} to batch:`, data); 
        batch.set(docRef, data); 
      }
      
      console.log('[Seeder] Attempting to commit batch...');
      await batch.commit();
      console.log('[Seeder] Batch commit successful!');
      setSeedStatusMessage('Successfully seeded Missional Scale Levels (20 documents created/updated with new fields).');
      toast.success('Missional Scale Levels seeded successfully with new fields!');

    } catch (error) {
      console.error('[Seeder] Error seeding Missional Scale Levels:', error);
      setSeedStatusMessage(`Error seeding: ${error.message || 'Unknown error'}. Check console for details.`);
      toast.error('Error seeding Missional Scale Levels. Check console.');
    } finally {
      console.log('[Seeder] Seeding function finished.');
      setIsSeeding(false);
    }
  };

  useEffect(() => {
    const fetchDisciples = async () => {
      if (!currentUser) return;
      
      const disciplesRef = collection(db, 'disciples');
      const disciplesSnap = await getDocs(disciplesRef);
      const disciplesList = disciplesSnap.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      setDisciples(disciplesList);
    };

    fetchDisciples();
  }, [currentUser]);

  useEffect(() => {
    const fetchOrgStats = async () => {
      if (!isSuperAdmin) return;

      try {
        // Get all organizations - don't filter by status in case status field doesn't exist
        const orgsRef = collection(db, 'organizations');
        const allOrgsSnap = await getDocs(orgsRef);
        
        // Count by status, defaulting to 'active' if status is missing
        let activeCount = 0;
        let pendingCount = 0;
        let subscribedCount = 0;
        let withActiveSubscriptionCount = 0;
        
        allOrgsSnap.docs.forEach(doc => {
          const data = doc.data();
          const status = data.status || 'active'; // Default to 'active' if status is missing
          
          if (status === 'active') {
            activeCount++;
          } else if (status === 'pending') {
            pendingCount++;
          } else {
            // If status is something else or missing, count as active
            activeCount++;
          }

          // Check subscription status
          const license = data.license;
          if (license) {
            subscribedCount++;
            // Check if subscription is active or trialing
            if (license.status === 'active' || license.status === 'trialing') {
              withActiveSubscriptionCount++;
            }
          }
        });

        setOrgStats({
          total: allOrgsSnap.size,
          active: activeCount,
          pending: pendingCount,
          subscribed: subscribedCount,
          withActiveSubscription: withActiveSubscriptionCount
        });
      } catch (error) {
        console.error('Error fetching organization stats:', error);
        // Set error state so user knows there's an issue
        setOrgStats({
          total: 0,
          active: 0,
          pending: 0,
          subscribed: 0,
          withActiveSubscription: 0
        });
      }
    };

    fetchOrgStats();
  }, [isSuperAdmin, refreshTrigger]);

  const handleOrgStatusChange = () => {
    // Refresh org stats immediately
    setOrgStats(prev => ({
      total: prev.total,  // Total stays the same since we're just changing status
      active: prev.active + 1,  // Increment active
      pending: Math.max(0, prev.pending - 1),  // Decrement pending, ensure it doesn't go below 0
      subscribed: prev.subscribed,  // Subscription status unchanged
      withActiveSubscription: prev.withActiveSubscription  // Active subscription status unchanged
    }));
  };

  const handleAddReminder = async () => {
    if (!selectedDisciple || !currentUser) return;
    
    const disciple = disciples.find(d => d.id === selectedDisciple);
    if (!disciple) return;

    try {
      await ReminderQueueTester.addTestReminder(
        currentUser.uid,
        disciple.id,
        disciple.name,
        minutesFromNow
      );
      refreshQueue();
    } catch (error) {
      console.error('Error adding test reminder:', error);
    }
  };

  const refreshQueue = async () => {
    if (!currentUser) return;
    try {
      const queue = await ReminderQueueTester.viewQueue(currentUser.uid);
      setCurrentQueue(queue);
    } catch (error) {
      console.error('Error fetching queue:', error);
    }
  };

  // Book abbreviation mapping
  const bookAbbreviations = {
    "Gen.": "Genesis", "Ex.": "Exodus", "Lev.": "Leviticus", "Num.": "Numbers", "Deut.": "Deuteronomy", "Josh.": "Joshua", "Judg.": "Judges", "Ruth": "Ruth",
    "1 Sam.": "1 Samuel", "1 Samuel": "1 Samuel", "2 Sam.": "2 Samuel", "2 Samuel": "2 Samuel",
    "1 Kgs.": "1 Kings", "1 Kings": "1 Kings", "2 Kgs.": "2 Kings", "2 Kings": "2 Kings",
    "1 Chron.": "1 Chronicles", "1 Chronicles": "1 Chronicles", "2 Chron.": "2 Chronicles", "2 Chronicles": "2 Chronicles",
    "Ezra": "Ezra", "Neh.": "Nehemiah", "Nehemiah": "Nehemiah", "Esth.": "Esther", "Esther": "Esther",
    "Job": "Job", "Ps.": "Psalms", "Psalm": "Psalms", "Psalms": "Psalms", "Prov.": "Proverbs", "Proverbs": "Proverbs",
    "Eccl.": "Ecclesiastes", "Ecclesiastes": "Ecclesiastes", "Song": "Song of Solomon", "Song of Solomon": "Song of Solomon",
    "Isa.": "Isaiah", "Isaiah": "Isaiah", "Jer.": "Jeremiah", "Jeremiah": "Jeremiah", "Lam.": "Lamentations", "Lamentations": "Lamentations",
    "Ezek.": "Ezekiel", "Ezekiel": "Ezekiel", "Dan.": "Daniel", "Daniel": "Daniel", "Hos.": "Hosea", "Hosea": "Hosea",
    "Joel": "Joel", "Amos": "Amos", "Obad.": "Obadiah", "Obadiah": "Obadiah", "Jonah": "Jonah", "Mic.": "Micah", "Micah": "Micah",
    "Nah.": "Nahum", "Nahum": "Nahum", "Hab.": "Habakkuk", "Habakkuk": "Habakkuk", "Zeph.": "Zephaniah", "Zephaniah": "Zephaniah",
    "Hag.": "Haggai", "Haggai": "Haggai", "Zech.": "Zechariah", "Zechariah": "Zechariah", "Mal.": "Malachi", "Malachi": "Malachi",
    // New Testament
    "Matt.": "Matthew", "Matthew": "Matthew", "Mark": "Mark", "Luke": "Luke", "John": "John", "Acts": "Acts",
    "Rom.": "Romans", "Romans": "Romans", "1 Cor.": "1 Corinthians", "1 Corinthians": "1 Corinthians", "2 Cor.": "2 Corinthians", "2 Corinthians": "2 Corinthians",
    "Gal.": "Galatians", "Galatians": "Galatians", "Eph.": "Ephesians", "Ephesians": "Ephesians", "Phil.": "Philippians", "Philippians": "Philippians",
    "Col.": "Colossians", "Colossians": "Colossians",
    "1 Thess.": "1 Thessalonians", "1 Thes.": "1 Thessalonians", "1 Thessalonians": "1 Thessalonians",
    "2 Thess.": "2 Thessalonians", "2 Thes.": "2 Thessalonians", "2 Thessalonians": "2 Thessalonians",
    "1 Tim.": "1 Timothy", "1 Timothy": "1 Timothy", "2 Tim.": "2 Timothy", "2 Timothy": "2 Timothy",
    "Titus": "Titus", "Philem.": "Philemon", "Philemon": "Philemon", "Heb.": "Hebrews", "Hebrews": "Hebrews",
    "James": "James", "1 Pet.": "1 Peter", "1 Peter": "1 Peter", "2 Pet.": "2 Peter", "2 Peter": "2 Peter",
    "1 John": "1 John", "2 John": "2 John", "3 John": "3 John", "Jude": "Jude", "Rev.": "Revelation", "Revelation": "Revelation"
  };

  function normalizeReference(ref) {
    if (!ref) return ref;
    const match = ref.match(/^((?:\d ?)?[A-Za-z. ]+?) ?(\d+:\d+(?:,\d+)?)/);
    if (match) {
      let book = match[1].trim();
      let rest = match[2];
      if (book === 'Psalm') book = 'Psalms';
      if (bookAbbreviations[book]) {
        return `${bookAbbreviations[book]} ${rest}`;
      }
    }
    return ref;
  }

  // Normalization function
  const normalizeVerseReferences = async () => {
    if (!window.confirm('Are you sure you want to normalize all verse references? This will update references with abbreviations to full book names.')) return;
    let updatedCount = 0;
    let checkedCount = 0;
    try {
      const versesRef = collection(db, 'verses');
      const snapshot = await getDocs(versesRef);
      const updates = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        let ref = data.reference;
        if (!ref) return;
        // Find abbreviation at start (handles 1/2/3 prefix and multi-word books)
        const normRef = normalizeReference(ref);
        if (normRef !== ref) {
          updates.push(updateDoc(doc(db, 'verses', docSnap.id), { reference: normRef }));
          updatedCount++;
        }
        checkedCount++;
      });
      await Promise.all(updates);
      setNormalizeResult({ updated: updatedCount, checked: checkedCount });
      setNormalizeModalOpen(true);
      toast.success(`Normalized ${updatedCount} of ${checkedCount} checked verses!`);
    } catch (err) {
      toast.error('Error normalizing verse references.');
      console.error(err);
    }
  };

  // Parse file when uploaded
  useEffect(() => {
    if (!cleanupFile) return;
    setCleanupParseError('');
    Papa.parse(cleanupFile, {
      header: true,
      delimiter: '\t',
      skipEmptyLines: true,
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          setCleanupParseError('No data found in file.');
          setCleanupPreview([]);
          return;
        }
        // Only keep Reference and Verse Text columns
        const previewRows = results.data
          .filter(row => row['Reference'] && (row['Verse Text'] || row['Text']))
          .map(row => ({
            reference: row['Reference'],
            text: row['Verse Text'] || row['Text']
          }));
        if (previewRows.length === 0) {
          setCleanupParseError('No valid Reference/Verse Text rows found.');
          setCleanupPreview([]);
          return;
        }
        setCleanupPreview(previewRows.slice(0, 10));
      },
      error: (err) => {
        setCleanupParseError('Error parsing file: ' + err.message);
        setCleanupPreview([]);
      }
    });
  }, [cleanupFile]);

  // Helper: error patterns
  const bibleBooks = [
    "Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy", "Joshua", "Judges", "Ruth",
    "1 Samuel", "2 Samuel", "1 Kings", "2 Kings", "1 Chronicles", "2 Chronicles", "Ezra",
    "Nehemiah", "Esther", "Job", "Psalms", "Proverbs", "Ecclesiastes", "Song of Solomon",
    "Isaiah", "Jeremiah", "Lamentations", "Ezekiel", "Daniel", "Hosea", "Joel", "Amos",
    "Obadiah", "Jonah", "Micah", "Nahum", "Habakkuk", "Zephaniah", "Haggai", "Zechariah", "Malachi",
    "Matthew", "Mark", "Luke", "John", "Acts", "Romans", "1 Corinthians", "2 Corinthians", "Galatians",
    "Ephesians", "Philippians", "Colossians", "1 Thessalonians", "2 Thessalonians", "1 Timothy", "2 Timothy",
    "Titus", "Philemon", "Hebrews", "James", "1 Peter", "2 Peter", "1 John", "2 John", "3 John", "Jude", "Revelation"
  ];
  function hasBibleBookRef(text) {
    return bibleBooks.some(book => new RegExp(`\\b${book} \\d+:\\d+\\b`).test(text));
  }
  function hasMultipleRefs(text) {
    return (text.match(/\b[A-Za-z]+ \d+:\d+\b/g) || []).length > 1;
  }
  function isProblematic(text) {
    if (!text) return false;
    if (text.includes('\\')) return true;
    if (/Bible Verses on \w+/i.test(text)) return true;
    if (/infaithblog\.com/i.test(text)) return true;
    if (hasBibleBookRef(text)) return true;
    if (hasMultipleRefs(text)) return true;
    if (/\\9[1234]'/.test(text)) return true;
    return false;
  }
  async function handleCleanupScan() {
    setCleanupScanLoading(true);
    setCleanupScanError('');
    try {
      // Build a map of uploaded corrections by robust normalized reference
      const uploadMap = new Map();
      cleanupPreview.forEach(row => {
        if (row.reference) {
          const normKey = normalizeReference(row.reference).trim().toLowerCase();
          uploadMap.set(normKey, row.text);
          console.log('[UPLOAD] Normalized:', row.reference, '->', normKey);
        }
      });
      // Fetch all verses
      const versesRef = collection(db, 'verses');
      const snapshot = await getDocs(versesRef);
      const results = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        const ref = data.reference;
        const text = data.text;
        const normRef = normalizeReference(ref).trim().toLowerCase();
        if (isProblematic(text)) {
          const replacement = uploadMap.get(normRef);
          console.log('[DB] Normalized:', ref, '->', normRef, '| Match:', !!replacement);
          results.push({
            id: docSnap.id,
            reference: ref,
            normalizedReference: normRef,
            currentText: text,
            replacementText: replacement || '',
            willReplace: !!replacement
          });
        }
      });
      setCleanupScanResults(results);
    } catch (err) {
      setCleanupScanError('Error scanning database: ' + err.message);
      setCleanupScanResults([]);
    } finally {
      setCleanupScanLoading(false);
    }
  }

  async function handleCleanupConfirm() {
    setCleanupProcessing(true);
    let replaced = 0;
    let skipped = 0;
    let flagged = 0;
    const replacedRefs = [];
    try {
      for (const row of cleanupScanResults) {
        if (row.willReplace && row.id) {
          await updateDoc(doc(db, 'verses', row.id), { text: row.replacementText });
          replaced++;
          replacedRefs.push(row.reference);
        } else if (!row.willReplace) {
          flagged++;
        } else {
          skipped++;
        }
      }
      setCleanupSummary({ replaced, skipped, flagged, replacedRefs });
      setCleanupConfirmOpen(false);
      setCleanupScanResults([]);
      setCleanupPreview([]);
      setCleanupFile(null);
    } catch (err) {
      setCleanupSummary({ error: 'Error updating verses: ' + err.message });
    } finally {
      setCleanupProcessing(false);
    }
  }

  async function handleCleanupUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const rows = text.split('\n')
        .map(line => line.trim())
        .filter(line => line && !line.startsWith('Reference\tText')) // Skip header
        .map(line => {
          const [reference, text] = line.split('\t');
          return { reference, text };
        });
      console.log('[UPLOAD] Parsed rows:', rows);
      setCleanupPreview(rows);
    } catch (err) {
      setCleanupParseError('Error parsing file: ' + err.message);
      setCleanupPreview([]);
    }
  }

  async function handleBibleUpload() {
    if (!bibleUploadFile) return;
    setBibleUploadLoading(true);
    setBibleUploadError('');
    setBibleUploadSummary(null);
    try {
      const text = await bibleUploadFile.text();
      const lines = text.split(/\r?\n/).filter(Boolean);
      const header = lines[0].toLowerCase();
      let refIdx = 0, textIdx = 1;
      if (!header.includes('reference') || !header.includes('text')) {
        throw new Error('File must have Reference and Text columns.');
      }
      if (header.split('\t')[0].includes('text')) {
        // Swap if columns are reversed
        refIdx = 1; textIdx = 0;
      }
      let count = 0;
      let batch = writeBatch(db);
      for (let i = 1; i < lines.length; ++i) {
        const cols = lines[i].split('\t');
        if (cols.length < 2) continue;
        const reference = cols[refIdx].trim();
        const verseText = cols[textIdx].trim();
        if (!reference || !verseText) continue;
        const verseDoc = doc(db, `bible/${bibleUploadId}/verses/${reference.replace(/\s/g, '_')}`);
        batch.set(verseDoc, {
          reference,
          text: verseText,
          active: false,
          enriched: false
        });
        count++;
        if (count % 450 === 0) {
          await batch.commit();
          batch = writeBatch(db);
        }
      }
      // Commit any remaining
      await batch.commit();
      // Set translation metadata
      await setDoc(doc(db, `bible/${bibleUploadId}`), {
        language: bibleUploadLang,
        translationName: bibleUploadName,
        abbreviation: bibleUploadAbbr,
        uploadDate: serverTimestamp()
      }, { merge: true });
      setBibleUploadSummary({ count });
    } catch (err) {
      console.error('Bible upload error:', err);
      setBibleUploadError(err.message);
    } finally {
      setBibleUploadLoading(false);
    }
  }

  // Helper to normalize references
  const normalizeRef = (ref) =>
    (ref || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');

  const handleWebReplacePreview = async () => {
    setWebReplaceLoading(true);
    setWebReplaceError('');
    setWebReplaceResult(null);
    try {
      // Fetch all VER records
      const versesSnap = await getDocs(collection(db, 'verses'));
      const verDocs = versesSnap.docs.filter(doc => /^VER\d+$/i.test(doc.id));
      console.log('First 10 VER doc IDs:', verDocs.slice(0, 10).map(doc => doc.id));
      console.log('First 10 VER doc data:', verDocs.slice(0, 10).map(doc => doc.data()));
      // Fetch all WEB Bible verses
      const webSnap = await getDocs(collection(db, 'bible/web/verses'));
      const webMap = new Map();
      webSnap.docs.forEach(doc => {
        const ref = doc.data().reference;
        if (ref) webMap.set(normalizeRef(ref), doc.data().text);
      });
      const verRefs = verDocs.map(doc => normalizeRef(doc.data().reference));
      console.log('First 20 normalized WEB references:', Array.from(webMap.keys()).slice(0, 20));
      console.log('First 20 normalized VER references:', verRefs.slice(0, 20));
      // Find matches
      const toUpdate = [];
      verDocs.forEach(doc => {
        const data = doc.data();
        const ref = data.reference;
        const normRef = normalizeRef(ref);
        if (!ref) {
          console.warn('VER record missing reference:', doc.id, data);
          return;
        }
        if (webMap.has(normRef)) {
          toUpdate.push({ id: doc.id, reference: ref, oldText: data.text, newText: webMap.get(normRef) });
        } else {
          console.log('No WEB match for normalized VER reference:', normRef);
        }
      });
      setWebReplacePreview({ count: toUpdate.length, toUpdate });
      console.log('WEB Replace Preview:', toUpdate);
    } catch (err) {
      setWebReplaceError('Error previewing replacements: ' + err.message);
      setWebReplacePreview(null);
    } finally {
      setWebReplaceLoading(false);
    }
  };

  // Execute the update
  const handleWebReplaceExecute = async () => {
    if (!webReplacePreview || !webReplacePreview.toUpdate) return;
    setWebReplaceLoading(true);
    setWebReplaceError('');
    try {
      let updated = 0;
      for (const rec of webReplacePreview.toUpdate) {
        await updateDoc(doc(db, 'verses', rec.id), { text: rec.newText });
        updated++;
      }
      setWebReplaceResult({ updated });
      setWebReplacePreview(null);
      console.log('WEB Replace Complete. Updated:', updated);
    } catch (err) {
      setWebReplaceError('Error updating records: ' + err.message);
    } finally {
      setWebReplaceLoading(false);
    }
  };

  // NEW: Handler for the backfill button
  const handleBackfill = async () => {
    if (!window.confirm('Are you sure you want to run this maintenance task? This will scan all users and update their active organization list.')) {
      return;
    }

    setIsBackfilling(true);
    const toastId = toast.loading('Starting user organization backfill...');

    try {
      const backfillFunction = httpsCallable(functions, 'backfillActiveOrgs');
      const result = await backfillFunction();
      const { data } = result;
      
      toast.success(
        `Backfill complete! Processed: ${data.usersProcessed}, Updated: ${data.usersUpdated}.`,
        { id: toastId, duration: 6000 }
      );

    } catch (error) {
      console.error('Error running backfill function:', error);
      toast.error(`An error occurred: ${error.message}`, { id: toastId });
    } finally {
      setIsBackfilling(false);
    }
  };

  const handleRunNextReminderCorrection = async () => {
    setCorrectionLoading(true);
    setCorrectionReport('');
    try {
      const fixFn = httpsCallable(functions, 'fixStuckNextReminderDates');
      const result = await fixFn();
      setCorrectionReport(result.data?.report || 'No report returned.');
    } catch (err) {
      setCorrectionReport('Error running correction: ' + (err.message || err.toString()));
    } finally {
      setCorrectionLoading(false);
    }
  };

  // Video compression settings handlers
  const loadVideoCompressionSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'appSettings', 'videoCompression'));
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        console.log('Loaded video compression settings:', settings);
        // Only set the fields we need for the form (ignore metadata fields)
        setVideoCompressionSettings({
          maxHeight: settings.maxHeight || 1280,
          targetBitrate: settings.targetBitrate || '1200k',
          codec: settings.codec || 'libx264',
          preset: settings.preset || 'veryfast',
          pixelFormat: settings.pixelFormat || 'yuv420p',
        });
      } else {
        console.log('No video compression settings found, using defaults');
      }
    } catch (error) {
      console.error('Error loading video compression settings:', error);
      toast.error('Error loading settings: ' + error.message);
    }
  };

  const saveVideoCompressionSettings = async () => {
    if (!currentUser?.uid) {
      toast.error('You must be logged in to save settings.');
      return;
    }

    setVideoCompressionLoading(true);
    try {
      console.log('Saving video compression settings:', videoCompressionSettings);
      const settingsData = {
        maxHeight: videoCompressionSettings.maxHeight || 1280,
        targetBitrate: videoCompressionSettings.targetBitrate || '1200k',
        codec: videoCompressionSettings.codec || 'libx264',
        preset: videoCompressionSettings.preset || 'veryfast',
        pixelFormat: videoCompressionSettings.pixelFormat || 'yuv420p',
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      };
      
      const settingsRef = doc(db, 'appSettings', 'videoCompression');
      await setDoc(settingsRef, settingsData);
      console.log('Video compression settings saved successfully');
      
      // Verify the save by reading it back (with timeout to avoid hanging)
      try {
        const verifyDoc = await Promise.race([
          getDoc(settingsRef),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Verification timeout')), 3000))
        ]);
        if (verifyDoc.exists()) {
          const savedData = verifyDoc.data();
          console.log('Verified: Settings were saved correctly', savedData);
          toast.success('Video compression settings saved!');
        } else {
          console.warn('Warning: Settings save verification failed - document not found');
          toast.error('Settings may not have saved. Please check console and try again.');
        }
      } catch (verifyError) {
        console.warn('Could not verify settings save (non-critical):', verifyError);
        toast.success('Video compression settings saved! (verification skipped)');
      }
      
      // Always close modal and reset loading
      setVideoCompressionLoading(false);
      setVideoCompressionModalOpen(false);
    } catch (error) {
      console.error('Error saving video compression settings:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Check for permission errors
      if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        toast.error('Permission denied. You may need Firestore rules for appSettings collection. Check console for details.');
        console.error('PERMISSION ERROR: The appSettings collection may not have Firestore rules allowing writes. Super admins need write access to appSettings/videoCompression.');
      } else {
        toast.error('Error saving settings: ' + (error.message || 'Unknown error'));
      }
      
      setVideoCompressionLoading(false);
      // Don't close modal on error so user can try again
    }
  };

  // Load settings when modal opens
  useEffect(() => {
    if (videoCompressionModalOpen) {
      loadVideoCompressionSettings();
    }
  }, [videoCompressionModalOpen]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          {isSuperAdmin ? 'Super Admin Dashboard' : 'Admin Dashboard'}
        </h1>

        {/* Super Admin Organization Management */}
        {isSuperAdmin && (
          <div className="mb-8 p-6 bg-white shadow-lg rounded-lg">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Organization Administration</h2>
            
            {/* Organization Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-700">Total Organizations</h4>
                <p className="text-2xl font-bold text-blue-800">{orgStats?.total || 0}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="text-sm font-medium text-green-700">Active Organizations</h4>
                <p className="text-2xl font-bold text-green-800">{orgStats?.active || 0}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="text-sm font-medium text-purple-700">Pending Approval</h4>
                <p className="text-2xl font-bold text-purple-800">{orgStats?.pending || 0}</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <h4 className="text-sm font-medium text-yellow-700">With Subscription</h4>
                <p className="text-2xl font-bold text-yellow-800">{orgStats?.subscribed || 0}</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg">
                <h4 className="text-sm font-medium text-emerald-700">Active Subscription</h4>
                <p className="text-2xl font-bold text-emerald-800">{orgStats?.withActiveSubscription || 0}</p>
              </div>
            </div>

            {/* Pending Organizations Section */}
            <div className="mt-6">
              <PendingOrganizations onStatusChange={handleOrgStatusChange} />
            </div>

            {/* Quick Actions */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link
                  to="/create-organization"
                  className="p-3 bg-blue-500 hover:bg-blue-600 rounded-lg text-left group text-white"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Create Organization</span>
                    <PlusIcon className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-sm text-blue-100 mt-1">Create a new organization</p>
                </Link>

                <button
                  onClick={() => navigate('/organizations')}
                  className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-left group"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Manage Organizations</span>
                    <Cog6ToothIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">View and manage all organizations</p>
                </button>

                <button
                  onClick={() => navigate('/organization-metrics')}
                  className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-left group"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Organization Metrics</span>
                    <ChartBarIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">View detailed analytics and reports</p>
                </button>

                <button
                  onClick={() => navigate('/organization-audit')}
                  className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-left group"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Audit Log</span>
                    <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">View organization changes and activity</p>
                </button>
              </div>
            </div>

            {/* Subscription Management */}
            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-700 mb-4">Subscription Management</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => navigate('/admin/subscriber-organizations')}
                  className="p-3 bg-yellow-100 hover:bg-yellow-200 rounded-lg text-left group"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Subscriber Organizations</span>
                    <svg className="h-5 w-5 text-yellow-600 group-hover:text-yellow-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <p className="text-sm text-yellow-700 mt-1">Manage subscriptions and early adopters</p>
                </button>

                <button
                  onClick={() => navigate('/admin/products-management')}
                  className="p-3 bg-purple-100 hover:bg-purple-200 rounded-lg text-left group"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Products Management</span>
                    <svg className="h-5 w-5 text-purple-600 group-hover:text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" />
                    </svg>
                  </div>
                  <p className="text-sm text-purple-700 mt-1">Manage Stripe products and pricing</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========================= */}
        {/* CONTENT MANAGEMENT SECTION */}
        {/* ========================= */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Content Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/content-tracks"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 rounded-lg">
                <BookOpenIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Content Tracks</h3>
                <p className="text-sm text-gray-500">Manage organization content tracks</p>
              </div>
            </Link>
            <Link
              to="/ai-management"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-purple-100 rounded-lg">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.57.393A9.065 9.065 0 0021.75 12a9.065 9.065 0 00-6.23-.693m-14.8.8a2.251 2.251 0 00-.659 1.591v5.714a2.25 2.25 0 00.659 1.591L5 19.5m14.8-4.8a2.251 2.251 0 01.659 1.591v5.714a2.25 2.25 0 01-.659 1.591L19.8 19.5" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">AI Management</h3>
                <p className="text-sm text-gray-500">Theological protection system and content checking</p>
              </div>
            </Link>
            <Link
              to="/admin/welcome-messages"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-green-100 rounded-lg">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Welcome Messages</h3>
                <p className="text-sm text-gray-500">Manage inspirational quotes shown on home screen</p>
              </div>
            </Link>
            {isSuperAdmin && (
              <Link
                to="/admin/surveys"
                className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-amber-100 rounded-lg">
                  <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Surveys</h3>
                  <p className="text-sm text-gray-500">Boilerplate surveys and question pool for Listen / Connect</p>
                </div>
              </Link>
            )}
            {isSuperAdmin && (
              <Link
                to="/prayers-management"
                className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-indigo-100 rounded-lg">
                  <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 1.567-3 3.5S10.343 15 12 15s3-1.567 3-3.5S13.657 8 12 8z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12c2.5-4 6-6 7-6s4.5 2 7 6c-2.5 4-6 6-7 6s-4.5-2-7-6z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Prayer Management</h3>
                  <p className="text-sm text-gray-500">Manage suggested prayers by missional level</p>
                </div>
              </Link>
            )}
          </div>
        </div>

        {/* ========================= */}
        {/* VERSE & BIBLE TOOLS AND REMINDER SECTIONS - SIDE BY SIDE */}
        {/* ========================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* VERSE & BIBLE TOOLS SECTION */}
          <div className="p-6 bg-white shadow-lg rounded-lg">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Verse & Bible Tools</h2>
            
            {/* Video Compression Settings */}
            <div className="mb-6 border-t pt-4 mt-4">
              <h3 className="text-lg font-medium text-gray-800 mb-2">Video Compression Settings</h3>
              <p className="text-sm text-gray-600 mb-3">
                Configure video compression settings for story videos. These settings affect all future video uploads.
              </p>
              <button
                onClick={() => setVideoCompressionModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Configure Video Compression
              </button>
            </div>
            {/* Seed Missional Scale Button */}
            <div className="mb-6 border-t pt-4 mt-4">
              <h3 className="text-lg font-medium text-gray-800 mb-2">Seed Missional Scale Levels</h3>
              <p className="text-sm text-gray-600 mb-3">
                Populate the 'missionalScaleLevels' collection with default phrases and descriptions (-10 to 9). This will overwrite existing documents in the collection.
              </p>
              <button
                onClick={seedMissionalScaleLevels}
                disabled={isSeeding}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSeeding ? 'Seeding...' : 'Seed Missional Scale Data'}
              </button>
              {seedStatusMessage && (
                <p className={`mt-3 text-sm ${seedStatusMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {seedStatusMessage}
                </p>
              )}
            </div>
            {/* Verse Update Button */}
            {isSuperAdmin && (
              <div className="mb-6 border-t pt-4 mt-4">
                <h3 className="text-lg font-medium text-gray-800 mb-2">Update Verse Schema</h3>
                <button
                  onClick={handleVerseUpdate}
                  disabled={isUpdating}
                  className="block w-full px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 text-center font-medium"
                >
                  {isUpdating ? 'Updating...' : 'Update Verses'}
                </button>
              </div>
            )}
            {/* Verse Enrichment Button */}
            {isSuperAdmin && (
              <div className="mb-6 border-t pt-4 mt-4">
                <h3 className="text-lg font-medium text-gray-800 mb-2">Enrich Verse Data</h3>
                <button
                  onClick={() => setIsEnrichmentModalOpen(true)}
                  disabled={isEnriching}
                  className="block w-full px-4 py-2.5 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition-colors duration-200 text-center font-medium"
                >
                  Enrich Verse Data
                </button>
              </div>
            )}
            {/* Normalize Verse References Button */}
            <div className="mb-6 border-t pt-4 mt-4">
              <h3 className="text-lg font-medium text-gray-800 mb-2">Normalize Verse References</h3>
              <button
                onClick={normalizeVerseReferences}
                className="block w-full px-4 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200 text-center font-medium"
              >
                Normalize Verse References
              </button>
            </div>
            {/* Bible Upload Button */}
            <button
              className="mb-4 px-4 py-2 bg-blue-700 text-white rounded hover:bg-blue-800 w-full"
              onClick={() => setBibleUploadModalOpen(true)}
            >
              Upload Bible Translation
            </button>
            <Link
              to="/search-bible-verses"
              className="mb-4 px-4 py-2 bg-indigo-700 text-white rounded hover:bg-indigo-800 block text-center"
            >
              Search & Add Bible Verses
            </Link>
            <button
              className="mb-4 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 w-full"
              onClick={() => {
                setWebReplaceModalOpen(true);
                setWebReplacePreview(null);
                setWebReplaceResult(null);
                setWebReplaceError('');
              }}
            >
              Replace System Verse Texts with WEB
            </button>
          </div>

          {/* REMINDER & NOTIFICATION TOOLS SECTION */}
          <div className="p-6 bg-white shadow-lg rounded-lg">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Reminder & Notification Tools</h2>
            
            {/* Reminder Tools */}
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-2">Reminder Diagnostics</h3>
              <button
                onClick={() => navigate('/reminder-queue-tester')}
                className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Open Reminder Queue Tester
              </button>
              <button
                onClick={() => navigate('/reminder-diagnostics')}
                className="w-full bg-teal-500 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded"
              >
                Open Reminder System Diagnostics
              </button>
              <button
                onClick={() => navigate('/reminders-by-disciple')}
                className="w-full bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded"
              >
                View Reminders by Disciple
              </button>
            </div>

            {/* Migrate Reminder Settings */}
            <div className="mb-6 border-t pt-4 mt-4">
              <h3 className="text-lg font-medium text-gray-800 mb-2">Migrate Reminder Settings</h3>
              <button
                onClick={handleMigration}
                disabled={isMigrating}
                className="block w-full px-4 py-2.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors duration-200 text-center font-medium"
              >
                {isMigrating ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Migrating...
                  </>
                ) : (
                  'Run Migration'
                )}
              </button>
              {migrationStatus && (
                <div className={`p-4 rounded-md mt-3 ${
                  migrationStatus.includes('failed') 
                    ? 'bg-red-50 text-red-700' 
                    : migrationStatus.includes('completed') 
                      ? 'bg-green-50 text-green-700'
                      : 'bg-blue-50 text-blue-700'
                }`}>
                  {migrationStatus}
                </div>
              )}
            </div>

            {/* Migrate nextNotificationDateAndTime - PHASE 5 */}
            <div className="mb-6 border-t pt-4 mt-4">
              <h3 className="text-lg font-medium text-gray-800 mb-2">Initialize Notification Dates (Phase 5)</h3>
              <p className="text-sm text-gray-600 mb-3">
                Backfill <code className="bg-gray-100 px-1 rounded">nextNotificationDateAndTime</code> for all existing disciples.
                This ensures your mobile app shows correct dates for all disciples. Run the local script from your terminal.
              </p>
              <button
                onClick={handleMigrateNextNotificationDateAndTime}
                className="block w-full px-4 py-2.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200 text-center font-medium"
              >
                Show Migration Instructions
              </button>
              {migrationNotificationDateStatus && (
                <div className={`p-4 rounded-md mt-3 whitespace-pre-line ${
                  migrationNotificationDateStatus.includes('failed') || migrationNotificationDateStatus.includes('Error')
                    ? 'bg-red-50 text-red-700'
                    : migrationNotificationDateStatus.includes('complete') || migrationNotificationDateStatus.includes('✅')
                      ? 'bg-green-50 text-green-700'
                      : 'bg-blue-50 text-blue-700'
                }`}>
                  {migrationNotificationDateStatus}
                </div>
              )}
            </div>

            {/* nextReminderDate Correction */}
            <div className="mb-6 border-t pt-4 mt-4">
              <h3 className="text-lg font-medium text-gray-800 mb-2">Next Reminder Date Correction</h3>
              <p className="text-sm text-gray-600 mb-3">
                Run correction for stuck nextReminderDate fields in the reminder system.
              </p>
              <button
                onClick={handleRunNextReminderCorrection}
                className="block w-full px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 text-center font-medium disabled:opacity-50"
                disabled={correctionLoading}
              >
                {correctionLoading ? 'Running...' : 'Run nextReminderDate Correction'}
              </button>
              {correctionReport && (
                <pre className="bg-gray-100 p-3 rounded text-sm whitespace-pre-wrap mt-3 border border-gray-200 overflow-x-auto">
                  {correctionReport}
                </pre>
              )}
            </div>
          </div>
        </div>

        {/* FCM Tester removed - web app uses email-only notifications */}

        {/* SuperAdmin Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 bg-white shadow-md rounded-lg">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Manage Groups</h2>
            <p className="text-gray-600 mb-6">Create and manage groups within organizations.</p>
            <div className="space-y-3">
              <Link
                to="/create-group"
                className="block w-full px-4 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200 text-center font-medium shadow-sm"
              >
                Create Group
              </Link>
              <Link
                to="/groups-list"
                className="block w-full px-4 py-2.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors duration-200 text-center font-medium shadow-sm"
              >
                View All Groups
              </Link>
            </div>
          </div>

          <div className="p-6 bg-white shadow-md rounded-lg">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Manage Users</h2>
            <p className="text-gray-600 mb-6">View and manage all users in the system.</p>
            <div className="space-y-3">
              <Link
                to="/users"
                className="block w-full px-4 py-2.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors duration-200 text-center font-medium shadow-sm"
              >
                View All Users
              </Link>
            </div>
          </div>

          <div className="p-6 bg-white shadow-md rounded-lg">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Manage Disciples</h2>
            <p className="text-gray-600 mb-6">View and manage all disciples across organizations.</p>
            <div className="space-y-3">
              <Link
                to="/add-disciple"
                className="block w-full px-4 py-2.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200 text-center font-medium shadow-sm"
              >
                Add New Disciple
              </Link>
              <Link
                to="/all-disciples"
                className="block w-full px-4 py-2.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors duration-200 text-center font-medium shadow-sm"
              >
                View All Disciples
              </Link>
            </div>
          </div>

          <div className="p-6 bg-white shadow-md rounded-lg">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Manage Resources</h2>
            <p className="text-gray-600 mb-6">Upload and manage verses and other resources.</p>
            <div className="space-y-3">
              <Link
                to="/upload-verses"
                className="block w-full px-4 py-2.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors duration-200 text-center font-medium"
              >
                Upload Verses (Enriched)
              </Link>
              <Link
                to="/upload-bible"
                className="block w-full px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 text-center font-medium"
              >
                Upload Complete Bible (TSV)
              </Link>
              <Link
                to="/search-verses-system"
                className="block w-full px-4 py-2.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors duration-200 text-center font-medium"
              >
                Search Verses
              </Link>
            </div>
          </div>
        </div>

        {/* Data Maintenance Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Cog6ToothIcon className="mr-3 h-6 w-6" />
            Data Maintenance
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Run periodic maintenance tasks to ensure data integrity.
          </p>
          <div className="space-y-4">
            <button
              onClick={handleBackfill}
              className="w-full bg-blue-100 text-blue-700 hover:bg-blue-200 px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isBackfilling}
            >
              {isBackfilling ? 'Processing...' : 'Backfill Active Orgs'}
            </button>
          </div>
        </div>
      </div>

      <EnrichmentModal
        isOpen={isEnrichmentModalOpen}
        onClose={() => {
          setIsEnrichmentModalOpen(false);
          setIsEnriching(false);
        }}
        onStart={() => setIsEnriching(true)}
        onComplete={() => setIsEnriching(false)}
        onConfirm={handleVerseEnrichment}
      />

      {/* Normalize Modal */}
      {normalizeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-sm w-full text-center">
            <h2 className="text-xl font-bold mb-4">Normalization Complete</h2>
            <p className="mb-2">Checked: <span className="font-semibold">{normalizeResult.checked}</span></p>
            <p className="mb-4">Normalized: <span className="font-semibold">{normalizeResult.updated}</span></p>
            <button
              onClick={() => setNormalizeModalOpen(false)}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Cleanup Confirmation Modal */}
      {cleanupConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Cleanup & Replace</h2>
            <p className="mb-2">This will update <span className="font-semibold">{cleanupScanResults.filter(r => r.willReplace).length}</span> verses, flag <span className="font-semibold">{cleanupScanResults.filter(r => !r.willReplace).length}</span> for manual review.</p>
            <ul className="mb-4 text-xs max-h-40 overflow-y-auto text-left">
              {cleanupScanResults.slice(0, 10).map((row, idx) => (
                <li key={idx} className="mb-1">
                  <span className="font-semibold">{row.reference}</span> {row.willReplace ? <span className="text-green-700">Will Replace</span> : <span className="text-red-700">Flagged</span>}
                </li>
              ))}
              {cleanupScanResults.length > 10 && <li>...and {cleanupScanResults.length - 10} more</li>}
            </ul>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setCleanupConfirmOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleCleanupConfirm}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                disabled={cleanupProcessing}
              >
                {cleanupProcessing ? 'Processing...' : 'Confirm & Replace'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cleanup Summary Modal */}
      {cleanupSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-bold mb-4">Cleanup Complete</h2>
            {cleanupSummary.error ? (
              <div className="text-red-600 mb-4">{cleanupSummary.error}</div>
            ) : (
              <>
                <p className="mb-2">Replaced: <span className="font-semibold">{cleanupSummary.replaced}</span></p>
                <p className="mb-2">Flagged (no replacement): <span className="font-semibold">{cleanupSummary.flagged}</span></p>
                <p className="mb-2">Skipped: <span className="font-semibold">{cleanupSummary.skipped}</span></p>
                {cleanupSummary.replacedRefs && cleanupSummary.replacedRefs.length > 0 && (
                  <div className="mb-2 text-xs max-h-32 overflow-y-auto text-left">
                    <div className="font-semibold mb-1">Replaced References:</div>
                    <ul>
                      {cleanupSummary.replacedRefs.slice(0, 10).map((ref, idx) => (
                        <li key={idx}>{ref}</li>
                      ))}
                      {cleanupSummary.replacedRefs.length > 10 && <li>...and {cleanupSummary.replacedRefs.length - 10} more</li>}
                    </ul>
                  </div>
                )}
              </>
            )}
            <button
              onClick={() => setCleanupSummary(null)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Bible Upload Modal */}
      {bibleUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-lg w-full">
            <h2 className="text-xl font-bold mb-4">Upload Bible Translation</h2>
            <label className="block mb-2 text-sm font-medium">Translation ID (unique, e.g. 'web', 'rvr1960')</label>
            <input
              className="mb-2 w-full border rounded px-2 py-1"
              value={bibleUploadId}
              onChange={e => setBibleUploadId(e.target.value)}
              placeholder="web"
            />
            <label className="block mb-2 text-sm font-medium">Language (e.g. 'en', 'es')</label>
            <input
              className="mb-2 w-full border rounded px-2 py-1"
              value={bibleUploadLang}
              onChange={e => setBibleUploadLang(e.target.value)}
              placeholder="en"
            />
            <label className="block mb-2 text-sm font-medium">Translation Name</label>
            <input
              className="mb-2 w-full border rounded px-2 py-1"
              value={bibleUploadName}
              onChange={e => setBibleUploadName(e.target.value)}
              placeholder="World English Bible"
            />
            <label className="block mb-2 text-sm font-medium">Abbreviation</label>
            <input
              className="mb-2 w-full border rounded px-2 py-1"
              value={bibleUploadAbbr}
              onChange={e => setBibleUploadAbbr(e.target.value)}
              placeholder="WEB"
            />
            <label className="block mb-2 text-sm font-medium">Upload File (.tsv, Reference & Text columns)</label>
            <input
              type="file"
              accept=".tsv,.csv,.txt"
              className="mb-4"
              onChange={e => setBibleUploadFile(e.target.files[0])}
            />
            {bibleUploadError && <div className="text-red-600 mb-2">{bibleUploadError}</div>}
            {bibleUploadSummary && (
              <div className="text-green-700 mb-2">Uploaded {bibleUploadSummary.count} verses!</div>
            )}
            <div className="flex gap-4 justify-end mt-4">
              <button
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                onClick={() => setBibleUploadModalOpen(false)}
                disabled={bibleUploadLoading}
              >
                Close
              </button>
              <button
                className="px-4 py-2 bg-blue-700 text-white rounded hover:bg-blue-800"
                onClick={handleBibleUpload}
                disabled={bibleUploadLoading || !bibleUploadFile}
              >
                {bibleUploadLoading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WEB Replace Modal */}
      {webReplaceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-lg w-full">
            <h2 className="text-xl font-bold mb-4">Replace System Verse Texts with WEB</h2>
            <p className="mb-2 text-gray-700">This will update the <b>text</b> field of all VER records in the system with the World English Bible (WEB) text, matching by reference. All other fields will be preserved.</p>
            {webReplaceError && <div className="text-red-600 mb-2">{webReplaceError}</div>}
            {!webReplacePreview && !webReplaceResult && (
              <button
                className="px-4 py-2 bg-blue-700 text-white rounded hover:bg-blue-800"
                onClick={handleWebReplacePreview}
                disabled={webReplaceLoading}
              >
                {webReplaceLoading ? 'Scanning...' : 'Preview Replacements'}
              </button>
            )}
            {webReplacePreview && (
              <div className="my-4">
                <div className="mb-2">Found <b>{webReplacePreview.count}</b> VER records to update.</div>
                <div className="max-h-40 overflow-y-auto text-xs bg-gray-50 rounded p-2 mb-2">
                  {webReplacePreview.toUpdate.slice(0, 10).map(rec => (
                    <div key={rec.id} className="mb-1">
                      <b>{rec.reference}</b><br/>
                      <span className="text-gray-500">Old:</span> {rec.oldText}<br/>
                      <span className="text-gray-500">New:</span> {rec.newText}
                    </div>
                  ))}
                  {webReplacePreview.count > 10 && <div className="text-gray-400">...and {webReplacePreview.count - 10} more</div>}
                </div>
                <button
                  className="px-4 py-2 bg-green-700 text-white rounded hover:bg-green-800"
                  onClick={handleWebReplaceExecute}
                  disabled={webReplaceLoading}
                >
                  {webReplaceLoading ? 'Updating...' : 'Execute Update'}
                </button>
              </div>
            )}
            {webReplaceResult && (
              <div className="my-4 text-green-700 font-semibold">
                Updated {webReplaceResult.updated} VER records with WEB text!
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                onClick={() => setWebReplaceModalOpen(false)}
                disabled={webReplaceLoading}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Compression Settings Modal */}
      {videoCompressionModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={(e) => {
            // Close modal if clicking on backdrop
            if (e.target === e.currentTarget) {
              setVideoCompressionModalOpen(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">Video Compression Settings</h2>
            <p className="text-sm text-gray-600 mb-4">
              Configure how story videos are compressed. These settings affect all future video uploads from both web and mobile apps.
            </p>
            
            {/* Important Notice */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">⚠️ Universal Settings</h3>
              <p className="text-sm text-blue-800 mb-2">
                These settings apply to <strong>all video uploads</strong> from both web and mobile apps. Changes affect compression for future uploads only.
              </p>
              <p className="text-sm text-blue-800">
                <strong>Performance Tips:</strong> Higher quality settings (larger resolutions, higher bitrates, slower presets) produce better video quality but take longer to process, use more storage, and may cause slower playback on mobile devices with limited bandwidth.
              </p>
            </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Height (pixels)
                  </label>
                  <input
                    type="number"
                    value={videoCompressionSettings.maxHeight}
                    onChange={(e) => setVideoCompressionSettings(prev => ({
                      ...prev,
                      maxHeight: parseInt(e.target.value) || 1280
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="1280"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum height in pixels. Width scales proportionally. 
                    <strong className="block mt-1 text-amber-700">
                      Recommended: 720-1280px. Higher values (1440p, 4K) will:
                    </strong>
                    <ul className="list-disc list-inside mt-1 text-xs text-amber-600">
                      <li>Significantly increase file size and storage costs</li>
                      <li>Slow down compression processing time</li>
                      <li>Cause slow loading/playback on mobile devices</li>
                      <li>Consume excessive mobile data for viewers</li>
                    </ul>
                    <span className="block mt-1 text-xs text-gray-600">
                      <strong>Mobile-optimized:</strong> 720px works well for most mobile devices and provides good quality with faster processing.
                    </span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Bitrate
                  </label>
                  <input
                    type="text"
                    value={videoCompressionSettings.targetBitrate}
                    onChange={(e) => setVideoCompressionSettings(prev => ({
                      ...prev,
                      targetBitrate: e.target.value
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="1200k"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Video bitrate (e.g., 1200k, 800k, 2M). Format: number followed by 'k' (kilobits) or 'M' (megabits).
                    <strong className="block mt-1 text-amber-700">
                      Recommended: 800k-1500k. Higher bitrates (2M+) will:
                    </strong>
                    <ul className="list-disc list-inside mt-1 text-xs text-amber-600">
                      <li>Produce larger files and higher storage costs</li>
                      <li>Require more bandwidth for playback</li>
                      <li>Cause buffering on slower mobile connections</li>
                      <li>Increase mobile data usage for viewers</li>
                    </ul>
                    <span className="block mt-1 text-xs text-gray-600">
                      <strong>Mobile-friendly:</strong> 800k-1200k provides good quality with reasonable file sizes and smooth playback on most networks.
                    </span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Codec
                  </label>
                  <select
                    value={videoCompressionSettings.codec}
                    onChange={(e) => setVideoCompressionSettings(prev => ({
                      ...prev,
                      codec: e.target.value
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="libx264">H.264 (libx264) - Recommended</option>
                    <option value="libx265">H.265 (libx265)</option>
                    <option value="libvpx-vp9">VP9 (libvpx-vp9)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Video codec used for compression.
                    <strong className="block mt-1 text-green-700">
                      Recommended: H.264 (libx264)
                    </strong>
                    <ul className="list-disc list-inside mt-1 text-xs text-gray-600">
                      <li><strong>H.264:</strong> Best compatibility across all devices (iOS, Android, web browsers). Fast encoding, universal support.</li>
                      <li><strong>H.265:</strong> Better compression but slower encoding, limited iOS support, may not work on all mobile devices.</li>
                      <li><strong>VP9:</strong> Good compression but very slow encoding, limited mobile browser support, not recommended for mobile apps.</li>
                    </ul>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preset
                  </label>
                  <select
                    value={videoCompressionSettings.preset}
                    onChange={(e) => setVideoCompressionSettings(prev => ({
                      ...prev,
                      preset: e.target.value
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="ultrafast">Ultrafast (fastest, larger file)</option>
                    <option value="superfast">Superfast</option>
                    <option value="veryfast">Veryfast (default, recommended)</option>
                    <option value="faster">Faster</option>
                    <option value="fast">Fast</option>
                    <option value="medium">Medium</option>
                    <option value="slow">Slow</option>
                    <option value="slower">Slower</option>
                    <option value="veryslow">Veryslow (slowest, smallest file)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Encoding speed vs. file size tradeoff. Faster presets encode quicker but produce larger files.
                    <strong className="block mt-1 text-amber-700">
                      Recommended: "veryfast" or "fast". Slower presets (medium, slow, slower, veryslow):
                    </strong>
                    <ul className="list-disc list-inside mt-1 text-xs text-amber-600">
                      <li>Can take 5-10+ minutes to process each video</li>
                      <li>May timeout for longer videos (5+ minutes)</li>
                      <li>Consume more Cloud Function resources</li>
                      <li>Only reduce file size marginally compared to "veryfast"</li>
                    </ul>
                    <span className="block mt-1 text-xs text-gray-600">
                      <strong>Best balance:</strong> "veryfast" provides fast processing with good compression for most use cases.
                    </span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pixel Format
                  </label>
                  <select
                    value={videoCompressionSettings.pixelFormat}
                    onChange={(e) => setVideoCompressionSettings(prev => ({
                      ...prev,
                      pixelFormat: e.target.value
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="yuv420p">YUV420P (most compatible, recommended)</option>
                    <option value="yuv422p">YUV422P</option>
                    <option value="yuv444p">YUV444P</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Pixel color format.
                    <strong className="block mt-1 text-green-700">
                      Recommended: YUV420P
                    </strong>
                    <span className="block mt-1 text-xs text-gray-600">
                      YUV420P is the standard format with universal device and browser support (iOS, Android, all web browsers). Other formats may cause playback issues on some mobile devices or browsers.
                    </span>
                  </p>
                </div>
              </div>

              {/* Recommended Settings Summary */}
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-sm font-semibold text-green-900 mb-2">✅ Recommended Mobile-Friendly Settings</h3>
                <ul className="text-xs text-green-800 space-y-1">
                  <li><strong>Max Height:</strong> 720px - 1280px (balanced quality and performance)</li>
                  <li><strong>Bitrate:</strong> 800k - 1200k (good quality with reasonable file sizes)</li>
                  <li><strong>Codec:</strong> H.264 (libx264) - universal compatibility</li>
                  <li><strong>Preset:</strong> veryfast - fast processing, good compression</li>
                  <li><strong>Pixel Format:</strong> YUV420P - works everywhere</li>
                </ul>
                <p className="text-xs text-green-700 mt-2 italic">
                  These settings ensure fast compression processing, good video quality, and smooth playback on both mobile devices and web browsers.
                </p>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setVideoCompressionModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                  disabled={videoCompressionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await saveVideoCompressionSettings();
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                  disabled={videoCompressionLoading}
                >
                  {videoCompressionLoading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        )}
    </Layout>
  );
};

export default AppAdmin;
